export const AuthorizationSubmissionDocumentationMarkdown = `
# Submit an authorization via API
This skill automates the process of submitting a prior authorization request, which typically takes humans on average 15 - 30 minutes to manually submit.

### When To Use

Use this skill once you have confirmed that a given prior authorization request is ready to be submitted to the payor. This should involve confirming the request has all the correct data fields and not missing any key information. Any incorrect or missing data could result in a denial from the payor which can cause patient delays.

The following payor portals are currently supported:
- Availity

Do not use this skill if your requested payor portal is not in the supported list above. More payor integrations coming very soon.

### How to Use

There are two types of submissions supported by this skill:
- Production: Use for submitting a real patient authorization submission to a payor.
- Mock: Use for testing your submissions before turning on in production.

# Production Submission:

### Input:
1. Only need to provide the Patient Order ID from an upstream block.

2. Ensure that the Test Mode toggle is turned off.

### How it works:
1. Retrieves the full Patient Order based on the ID provided from the upstream block.

2. Filters down to relevant data fields based on payor requirements.

3. Submits the prior authorization to the payor using the optimal method that maximizes success rates while minimizing latencies.

This can take up to a few minutes on average.

### Outputs:
Returns the full response from the payor including the authorization status such as Approved, Denied, Pending, or Exception with as much detail as possible.

The specific response will vary by payor.

Output Example

~~~json
{
  "statusingResponse": {
    "serviceReviewLocation": null,
    "serviceReviewResponseBody": {
      "id": "12345678",
      "links": {
        "self": {
          "href": "http://api.availity.com/availity/v2/service-reviews/12345678"
        }
      },
      "payer": {
        "id": "BCBSF",
        "name": "FLORIDA BLUE",
        "phone": "8009555692"
      },
      "status": "Pended",
      "patient": {
        "lastName": "BOBBY",
        "birthDate": "1926-08-10",
        "firstName": "TABLES",
        "subscriberRelationship": "Self",
        "subscriberRelationshipCode": "18"
      },
      "fromDate": "2015-01-22",
      "quantity": "0",
      "deletable": false,
      "diagnoses": [
        {
          "code": "78900",
          "date": "2015-01-22",
          "value": "Abdmnal pain unspcf site",
          "qualifier": "International Classification of Diseases Clinical Modification &#x28;ICD-9-CM&#x29; Diagnosis",
          "qualifierCode": "BF"
        }
      ],
      "updatable": false,
      "customerId": "1234",
      "statusCode": "A4",
      "subscriber": {
        "lastName": "BOBBY",
        "memberId": "ASBA1274712",
        "firstName": "TABLES"
      },
      "createdDate": "2016-10-22T18:38:25.000+0000",
      "requestType": "Admission Review",
      "serviceType": "Medical Care",
      "updatedDate": "2016-10-22T18:38:28.000+0000",
      "quantityType": "Days",
      "serviceLevel": "Elective",
      "admissionType": "Emergency",
      "statusReasons": [
        {
          "code": "0V",
          "value": "Requires Medical Review"
        }
      ],
      "expirationDate": "2016-10-23T18:38:25.000+0000",
      "placeOfService": "Inpatient Hospital",
      "referenceNumber": "REF15012656077",
      "requestTypeCode": "AR",
      "serviceTypeCode": "1",
      "quantityTypeCode": "DY",
      "serviceLevelCode": "E",
      "admissionTypeCode": "1",
      "placeOfServiceCode": "21",
      "renderingProviders": [
        {
          "npi": "1234567893",
          "city": "VENICE",
          "role": "Attending Physician",
          "phone": "8001229994",
          "state": "Florida",
          "zipCode": "342852900",
          "lastName": "SMITH",
          "roleCode": "71",
          "firstName": "JIM",
          "specialty": "Surgery",
          "stateCode": "FL",
          "addressLine1": "123 MAIN ST",
          "specialtyCode": "208600000X"
        },
        {
          "npi": "1234567893",
          "city": "VENICE",
          "role": "Facility",
          "phone": "8007772621",
          "state": "Florida",
          "zipCode": "342852900",
          "lastName": "JIMS DISCOUNT SURGERY",
          "roleCode": "FA",
          "specialty": "General Acute Care Hospital",
          "stateCode": "FL",
          "addressLine1": "123 MAIN ST",
          "specialtyCode": "282N00000X"
        }
      ],
      "requestingProvider": {
        "npi": "1234567893",
        "city": "JACKSONVILLE",
        "phone": "1112223334",
        "state": "Florida",
        "zipCode": "322231112",
        "lastName": "JIMS DISCOUNT MEDICAL",
        "specialty": "General Acute Care Hospital",
        "stateCode": "FL",
        "contactName": "BOB",
        "submitterId": "G77124",
        "addressLine1": "123 MAIN ST",
        "specialtyCode": "282N00000X"
      },
      "shortFormIndicator": false,
      "certificationEffectiveDate": "2015-01-22",
      "certificationExpirationDate": "2015-01-22"
    }
  },
  "submissionResponse": {
    "serviceReviewLocation": "http://api.availity.com/availity/v2/service-reviews/12345678",
    "serviceReviewResponseBody": null
  }
}
~~~

# Mock Submission:

### Input:
1. Patient Order ID is retrieved from an upstream block.

2. Ensure that the Test Mode toggle is turned on.

3. Only fill in either Mock Scenario ID or use Patient Last Name.

- Mock Scenario ID: Can be used to request a mocked canned response from the payor and test connectivity to Availity. Refer to Service Reviews demo response scenarios at https://developer.availity.com/partner/documentation#c_service_reviews for more details.

- Patient Last Name: Can be used to specify a mock response flag for testing database updates. Can only be used with patients that have a last name containing the flag 'zzzTestAuth'.

### How it works:
Mock Scenario ID: Retrieves a mocked canned response from the payor portal from the demo client. This is useful for testing connectivity to Availity from the block. 

Patient Last Name: Attempts to submit a request using the test order's information stored in the database, and updates the test order with the response from the payor. This is useful for testing database updates and ensuring that the test order has all the information necessary to construct a request.

### Outputs:
Returns the full response from the payor depending on the mockScenarioId or patientLastName provided. Possible outputs include statuses such as Approved, Denied, Pending, or Exception with as much detail as possible.

The specific response will not correspond exactly to any payor configurations as it is a canned general response from Availity. 

Output Example

~~~json
{
  "statusingResponse": {
    "serviceReviewLocation": null,
    "serviceReviewResponseBody": {
      "id": "12345678",
      "links": {
        "self": {
          "href": "http://api.availity.com/availity/v2/service-reviews/12345678"
        }
      },
      "payer": {
        "id": "BCBSF",
        "name": "FLORIDA BLUE",
        "phone": "8009555692"
      },
      "status": "Pended",
      "patient": {
        "lastName": "BOBBY",
        "birthDate": "1926-08-10",
        "firstName": "TABLES",
        "subscriberRelationship": "Self",
        "subscriberRelationshipCode": "18"
      },
      "fromDate": "2015-01-22",
      "quantity": "0",
      "deletable": false,
      "diagnoses": [
        {
          "code": "78900",
          "date": "2015-01-22",
          "value": "Abdmnal pain unspcf site",
          "qualifier": "International Classification of Diseases Clinical Modification &#x28;ICD-9-CM&#x29; Diagnosis",
          "qualifierCode": "BF"
        }
      ],
      "updatable": false,
      "customerId": "1234",
      "statusCode": "A4",
      "subscriber": {
        "lastName": "BOBBY",
        "memberId": "ASBA1274712",
        "firstName": "TABLES"
      },
      "createdDate": "2016-10-22T18:38:25.000+0000",
      "requestType": "Admission Review",
      "serviceType": "Medical Care",
      "updatedDate": "2016-10-22T18:38:28.000+0000",
      "quantityType": "Days",
      "serviceLevel": "Elective",
      "admissionType": "Emergency",
      "statusReasons": [
        {
          "code": "0V",
          "value": "Requires Medical Review"
        }
      ],
      "expirationDate": "2016-10-23T18:38:25.000+0000",
      "placeOfService": "Inpatient Hospital",
      "referenceNumber": "REF15012656077",
      "requestTypeCode": "AR",
      "serviceTypeCode": "1",
      "quantityTypeCode": "DY",
      "serviceLevelCode": "E",
      "admissionTypeCode": "1",
      "placeOfServiceCode": "21",
      "renderingProviders": [
        {
          "npi": "1234567893",
          "city": "VENICE",
          "role": "Attending Physician",
          "phone": "8001229994",
          "state": "Florida",
          "zipCode": "342852900",
          "lastName": "SMITH",
          "roleCode": "71",
          "firstName": "JIM",
          "specialty": "Surgery",
          "stateCode": "FL",
          "addressLine1": "123 MAIN ST",
          "specialtyCode": "208600000X"
        },
        {
          "npi": "1234567893",
          "city": "VENICE",
          "role": "Facility",
          "phone": "8007772621",
          "state": "Florida",
          "zipCode": "342852900",
          "lastName": "JIMS DISCOUNT SURGERY",
          "roleCode": "FA",
          "specialty": "General Acute Care Hospital",
          "stateCode": "FL",
          "addressLine1": "123 MAIN ST",
          "specialtyCode": "282N00000X"
        }
      ],
      "requestingProvider": {
        "npi": "1234567893",
        "city": "JACKSONVILLE",
        "phone": "1112223334",
        "state": "Florida",
        "zipCode": "322231112",
        "lastName": "JIMS DISCOUNT MEDICAL",
        "specialty": "General Acute Care Hospital",
        "stateCode": "FL",
        "contactName": "BOB",
        "submitterId": "G77124",
        "addressLine1": "123 MAIN ST",
        "specialtyCode": "282N00000X"
      },
      "shortFormIndicator": false,
      "certificationEffectiveDate": "2015-01-22",
      "certificationExpirationDate": "2015-01-22"
    }
  },
  "submissionResponse": {
    "serviceReviewLocation": "http://api.availity.com/availity/v2/service-reviews/12345678",
    "serviceReviewResponseBody": null
  }
}
~~~


### Other Useful Tips
- Utilize mock data for comprehensive testing of the authorization flow without incurring the costs or delays of live API calls. Ensure mock data accurately reflects potential response scenarios including success and different types of failures.

- Test all possible submission output scenarios and ensure all of them are accounted for in the flow. For example, ensure you catch any failure responses and appropriately route back to human fallback or retry with corrected information.

- Review the detailed error logs to diagnose and resolve any issues encountered.


### Error Conditions
- Missing Order ID: The block will stop if a valid order ID is not provided. 

- API Request Errors: Errors during communication with the authorization API (e.g., network issues, invalid credentials) will be reported.

- Unexpected Status Codes: If the authorization API returns an unexpected or uninterpretable status code, the block will stop and report an error.

- Database Update Failures: If there are errors updating the database with the authorization status, the block will report an error.

### Best Practices
- Thoroughly test the block: Use the mock mode extensively during development and testing.

- Map input fields carefully: Ensure that all required fields are correctly mapped from your workflow to the block.

- Monitor error logs: Regularly review the error logs to identify and resolve issues promptly.

- Use version control: Manage your workflows and configurations effectively using a version control system.

### Block Limitations
- The block only supports a limited number of payors.

- The block currently depends on a specific version of the payor API. Changes to that API may require updates to the block.

- Mock responses are based on examples; complex or unusual API responses may not be fully simulated.

### Local Development 
For testing on local development, you will need access to the client ID and client secret for the Availity API. 

You can raise an IT request on Slack to get access to the 1Password vault where the credentials are stored.
`;
